/**
 * Sunglasses family picker — lightweight client-side variant switching.
 * No section fetch, no gallery rebuild. Event delegation on document.
 */
(function () {
  'use strict';

  if (window.MPSunglassesFamily?.initialized) return;

  const INITIAL_PRELOAD_DELAY_MS = 1800;
  const catalogById = new Map();
  let saveLabel = 'SAVE';

  window.MPSunglassesFamily = { initialized: true, catalogById };

  function runWhenIdle(callback) {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(callback, { timeout: 3000 });
    } else {
      setTimeout(callback, 150);
    }
  }

  function getProductInfo(node) {
    return node?.closest?.('product-info') || null;
  }

  function readCatalog(productInfo) {
    const script = productInfo?.querySelector('[data-mp-family-catalog]');
    if (!script) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  function loadCatalog(productInfo) {
    const data = readCatalog(productInfo);
    if (!data?.variants?.length) return null;

    saveLabel = data.saveLabel || 'SAVE';
    data.variants.forEach(function (variant) {
      catalogById.set(String(variant.id), variant);
    });

    return data;
  }

  function resolveVariantId(productInfo) {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get('variant');
    if (fromUrl && catalogById.has(fromUrl)) return fromUrl;

    const catalog = readCatalog(productInfo);
    if (catalog?.activeVariantId && catalogById.has(String(catalog.activeVariantId))) {
      return String(catalog.activeVariantId);
    }

    const input = productInfo.querySelector('input[name="id"]');
    if (input?.value && catalogById.has(input.value)) return input.value;

    return catalog?.variants?.[0]?.id ? String(catalog.variants[0].id) : null;
  }

  function updateVariantInputs(productInfo, variantId) {
    productInfo
      .querySelectorAll(`#product-form-${productInfo.dataset.section}, #product-form-installment-${productInfo.dataset.section}`)
      .forEach(function (formRoot) {
        const input = formRoot.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
  }

  function updateSelectedLabel(productInfo, label) {
    const target = productInfo.querySelector('[data-selected-value]');
    if (target) target.textContent = label;
  }

  function updateSwatchState(productInfo, variantId) {
    productInfo.querySelectorAll('[data-mp-family-variant]').forEach(function (button) {
      const isActive = button.dataset.mpFamilyVariant === variantId;
      button.classList.toggle('mp-sunglasses-family-picker__swatch--active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function updateSaveRow(productInfo, variant) {
    const row = productInfo.querySelector('.mp-sunglasses-price-row');
    if (!row) return;

    const sale = row.querySelector('.mp-sunglasses-sale');
    const compare = row.querySelector('.mp-sunglasses-compare');
    const save = row.querySelector('.mp-sunglasses-save');

    if (sale) sale.textContent = variant.priceFormatted;

    const hasCompare = variant.compareAtPrice > variant.price;
    if (compare) {
      compare.textContent = variant.compareAtFormatted || '';
      compare.hidden = !hasCompare;
    }
    if (save) {
      save.textContent = variant.saveFormatted || '';
      save.hidden = !hasCompare;
    }
  }

  function updateAvailability(productInfo, variant) {
    const productForm = productInfo.querySelector('product-form');
    const submitButton = productInfo.querySelector('[id^="ProductSubmitButton-"]');
    const submitText = submitButton?.querySelector('span');

    if (productForm && typeof productForm.toggleSubmitButton === 'function') {
      productForm.toggleSubmitButton(
        !variant.available,
        variant.available ? undefined : window.variantStrings?.soldOut
      );
    } else if (submitButton && submitText) {
      if (variant.available) {
        submitButton.removeAttribute('disabled');
        submitText.textContent = window.variantStrings?.addToCart || 'Add to cart';
      } else {
        submitButton.setAttribute('disabled', 'disabled');
        submitText.textContent = window.variantStrings?.soldOut || 'Sold out';
      }
    }

    const variantInput = productInfo.querySelector('input.product-variant-id, input[name="id"]');
    if (variantInput) {
      variantInput.disabled = !variant.available;
    }
  }

  function updateFeaturedImage(productInfo, variant) {
    if (!variant.featuredImageUrl) return;

    const activeSlide = productInfo.querySelector('.product__media-item.is-active');
    if (!activeSlide) return;

    const img =
      activeSlide.querySelector('.product__modal-opener--image img') ||
      activeSlide.querySelector('.product__media img') ||
      activeSlide.querySelector('img');

    if (!img) return;

    img.src = variant.featuredImageUrl;
    img.removeAttribute('data-mp-defer-src');
    img.removeAttribute('data-mp-defer-srcset');
    img.srcset = '';
    img.alt = variant.displayLabel || '';
    img.dataset.mpLoaded = 'true';
    img.classList.remove('mp-deferred-media');

    const spinner = activeSlide.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function updateUrl(productInfo, variant) {
    const url = `${variant.productUrl}?variant=${variant.id}`;

    productInfo.dataset.url = variant.productUrl;
    productInfo.dataset.productId = String(variant.productId);

    const variantSelects = productInfo.querySelector('variant-selects[data-mp-sunglasses-family]');
    if (variantSelects) {
      variantSelects.dataset.productHandle = variant.productHandle;
      variantSelects.dataset.productId = String(variant.productId);
      variantSelects.dataset.selectedPriceAmount = String((variant.price || 0) / 100);
    }

    const shareButton = productInfo.querySelector('share-button');
    if (shareButton?.updateUrl) {
      shareButton.updateUrl(`${window.shopUrl || ''}${url}`);
    }

    if (productInfo.dataset.updateUrl !== 'false') {
      window.history.replaceState({}, '', url);
    }
  }

  function updateSelectedVariantJson(productInfo, variant) {
    const script = productInfo.querySelector('[data-selected-variant]');
    if (!script) return;

    script.textContent = JSON.stringify({
      id: variant.id,
      available: variant.available,
      price: variant.price,
      compare_at_price: variant.compareAtPrice,
      featured_media: variant.featuredImageUrl ? { preview_image: { src: variant.featuredImageUrl } } : null,
    });
  }

  function applyVariant(productInfo, variantId) {
    const variant = catalogById.get(String(variantId));
    if (!variant || !productInfo) return;

    updateSwatchState(productInfo, String(variant.id));
    updateSelectedLabel(productInfo, variant.displayLabel);
    updateSaveRow(productInfo, variant);
    updateFeaturedImage(productInfo, variant);
    updateVariantInputs(productInfo, String(variant.id));
    updateAvailability(productInfo, variant);
    updateUrl(productInfo, variant);
    updateSelectedVariantJson(productInfo, variant);
  }

  function preloadFeaturedImages(catalog) {
    if (!catalog?.variants?.length) return;

    catalog.variants.forEach(function (variant) {
      if (!variant.featuredImageUrl) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = variant.featuredImageUrl;
    });
  }

  function scheduleFeaturedPreload(productInfo) {
    const catalog = readCatalog(productInfo);
    if (!catalog) return;

    window.addEventListener(
      'load',
      function () {
        setTimeout(function () {
          runWhenIdle(function () {
            preloadFeaturedImages(catalog);
          });
        }, INITIAL_PRELOAD_DELAY_MS);
      },
      { once: true }
    );
  }

  function initProductInfo(productInfo) {
    if (!productInfo.querySelector('[data-mp-sunglasses-family]')) return;
    if (productInfo.dataset.mpSunglassesFamilyBound === 'true') return;

    const catalog = loadCatalog(productInfo);
    if (!catalog) return;

    productInfo.dataset.mpSunglassesFamilyBound = 'true';

    const variantId = resolveVariantId(productInfo);
    if (variantId) applyVariant(productInfo, variantId);

    scheduleFeaturedPreload(productInfo);
  }

  function handleSwatchClick(event) {
    const button = event.target.closest('[data-mp-family-variant]');
    if (!button) return;

    const productInfo = getProductInfo(button);
    if (!productInfo?.querySelector('[data-mp-sunglasses-family]')) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = button.dataset.mpFamilyVariant;
    if (!variantId || !catalogById.has(variantId)) return;

    applyVariant(productInfo, variantId);
  }

  document.addEventListener('click', handleSwatchClick);

  document.addEventListener('product-info:loaded', function (event) {
    if (event.target?.matches?.('product-info')) {
      initProductInfo(event.target);
    }
  });

  document.querySelectorAll('product-info').forEach(initProductInfo);
})();
